import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import stripeService from '../../services/stripeService';
import { useTenant } from '../../contexts/TenantContext';
import api from '../../services/api';
import authService from '../../services/authService';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Divider, Alert, CircularProgress, Grid,
  Card, CardContent, CardHeader, List, ListItem, ListItemText, ListItemIcon,
  Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Container
} from '@mui/material';

// Import Material UI icons
import {
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  CreditCard as CreditCardIcon,
  KeyboardArrowRight as ArrowIcon,
  Warning as WarningIcon,
  AccessTime as ClockIcon,
  Cancel as CancelIcon,
  Upgrade as UpgradeIcon,
  ReceiptLong as ReceiptIcon,
  Event as EventIcon,
  AccountCircle as UserIcon
} from '@mui/icons-material';

// Plan Feature component to show features with checkmarks
const PlanFeature = ({ feature }) => (
  <ListItem>
    <ListItemIcon sx={{ minWidth: 36 }}>
      <CheckCircleIcon fontSize="small" color="primary" />
    </ListItemIcon>
    <ListItemText primary={feature} />
  </ListItem>
);

// Format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Format currency with dollar sign
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const SubscriptionManagement = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get subscription plans using the JWT-authenticated API call
        console.log('Fetching subscription plans...');
        const token = authService.getToken();
        
        if (!token) {
          console.error('No authentication token found');
          setError("Authentication error. Please log in again.");
          setLoading(false);
          return;
        }
        
        // First try to get actual plans directly from API
        let plansData = [];
        try {
          const directPlansResponse = await api.get(
            `/v1/stripe/admin/available-plans`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Direct plans API response:', directPlansResponse);
          
          if (directPlansResponse && directPlansResponse.data) {
            // Extract plans data from response, handling various response formats
            plansData = Array.isArray(directPlansResponse.data) 
              ? directPlansResponse.data 
              : (directPlansResponse.data.data || []);
              
            if (plansData.length > 0) {
              // Filter out any inactive plans
              plansData = plansData.filter(plan => plan.active !== false);
              
              // Filter out duplicate plans by ID
              const uniquePlansMap = new Map();
              
              // Use a Map to identify unique plans - prioritize plans with more details
              plansData.forEach(plan => {
                // Use id, planId, or priceId as key for deduplication
                const planKey = plan.id || plan.planId || plan.priceId;
                
                if (planKey) {
                  if (!uniquePlansMap.has(planKey) || 
                      (plan.features && (!uniquePlansMap.get(planKey).features || 
                                        plan.features.length > uniquePlansMap.get(planKey).features.length))) {
                    uniquePlansMap.set(planKey, plan);
                  }
                }
              });
              
              // Convert Map back to array
              plansData = Array.from(uniquePlansMap.values());
              
              // Sort plans by price (lowest to highest)
              const sortedPlans = [...plansData].sort((a, b) => a.price - b.price);
              console.log(`Successfully loaded ${sortedPlans.length} unique, active plans from API`);
              setPlans(sortedPlans);
            }
          }
        } catch (directPlansError) {
          console.error("Direct plans API call failed:", directPlansError);
          // Fall back to service method
          const plansResponse = await stripeService.getAdminSubscriptionPlans();
          
          console.log('Fallback plans response:', plansResponse);
          
          if (plansResponse && plansResponse.success && Array.isArray(plansResponse.data)) {
            console.log(`Received ${plansResponse.data.length} plans from service API`);
            if (plansResponse.data.length > 0) {
              // Filter out any inactive plans
              let fallbackPlans = plansResponse.data.filter(plan => plan.active !== false);
              
              // Filter out duplicate plans
              const uniquePlansMap = new Map();
              
              // Use a Map to identify unique plans
              fallbackPlans.forEach(plan => {
                // Use id, planId, or priceId as key for deduplication
                const planKey = plan.id || plan.planId || plan.priceId;
                
                if (planKey) {
                  if (!uniquePlansMap.has(planKey) || 
                      (plan.features && (!uniquePlansMap.get(planKey).features || 
                                        plan.features.length > uniquePlansMap.get(planKey).features.length))) {
                    uniquePlansMap.set(planKey, plan);
                  }
                }
              });
              
              // Convert Map back to array
              fallbackPlans = Array.from(uniquePlansMap.values());
              
              // Sort plans by price (lowest to highest)
              plansData = [...fallbackPlans].sort((a, b) => a.price - b.price);
              setPlans(plansData);
              console.log(`Successfully loaded ${plansData.length} unique, active plans from service API`);
              
              // Log data source
              if (plansResponse.source === 'default') {
                console.warn('Warning: Using default plans - could not connect to API');
              }
            } else {
              setError("No subscription plans are currently available. Please contact support for assistance.");
            }
          } else {
            setError("Failed to load subscription plans. Please refresh and try again.");
          }
        }
        
        // Get current subscription - try direct API call first
        try {
          console.log('Fetching current tenant subscription with direct API call');
          const subscriptionResponse = await api.get(
            `/v1/stripe/tenant/subscription`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Direct tenant subscription response:', subscriptionResponse);
          
          if (subscriptionResponse && subscriptionResponse.data) {
            const responseData = subscriptionResponse.data;
            const subData = responseData.data || responseData;
            
            // Find the matching plan from available plans for features
            let planFeatures = [];
            const matchingPlan = plansData.find(p => 
              p.id === subData.stripePriceId || 
              p.id === subData.plan?.id ||
              p.planId === subData.plan?.planId
            );
            
            if (matchingPlan && matchingPlan.features) {
              planFeatures = matchingPlan.features;
            } else if (subData.plan && Array.isArray(subData.plan.features)) {
              planFeatures = subData.plan.features;
            }
            
            const subscriptionPrice = 
              subData.plan?.amount || 
              subData.plan?.price || 
              (matchingPlan ? matchingPlan.price : null);
            
            // Set subscription with actual data from the API response
            setSubscription({
              id: subData._id || subData.id || subData.stripeSubscriptionId,
              stripeSubscriptionId: subData.stripeSubscriptionId,
              planId: subData.stripePriceId || subData.plan?.id,
              planName: subData.plan?.name || (matchingPlan ? matchingPlan.name : 'Standard Plan'),
              status: subData.status || 'active',
              currentPeriodStart: subData.currentPeriodStart,
              currentPeriodEnd: subData.currentPeriodEnd,
              cancelAtPeriodEnd: subData.cancelAtPeriodEnd || false,
              amount: subscriptionPrice,
              interval: subData.interval || subData.plan?.interval || 'month',
              features: planFeatures
            });
            
            // Get payment history
            try {
              console.log('Fetching payment history');
              const paymentHistoryResponse = await api.get(
                `/v1/stripe/tenant/payment-history`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              console.log('Payment history response:', paymentHistoryResponse);
              
              if (paymentHistoryResponse && paymentHistoryResponse.data) {
                const paymentData = Array.isArray(paymentHistoryResponse.data) 
                  ? paymentHistoryResponse.data 
                  : (paymentHistoryResponse.data.data || []);
                  
                setPaymentHistory(paymentData);
              } else if (subscriptionResponse.data.payments && 
                        Array.isArray(subscriptionResponse.data.payments)) {
                // Use payments from subscription response if available
                setPaymentHistory(subscriptionResponse.data.payments);
              } else {
                console.warn('No payment history found in API response');
                setPaymentHistory([]);
              }
            } catch (paymentError) {
              console.error("Failed to fetch payment history:", paymentError);
              setPaymentHistory([]);
            }
            
            setLoading(false);
            return;
          }
        } catch (subError) {
          console.error("Direct subscription API call failed:", subError);
        }
          
        // If direct API call fails, try using the stripeService method as fallback
        try {
          console.log('Trying fallback subscription method via stripeService');
          const fallbackResponse = await stripeService.getTenantSubscription();
          console.log('Fallback subscription response:', fallbackResponse);
          
          if (fallbackResponse && fallbackResponse.data) {
            const subData = fallbackResponse.data;
            
            // Find the matching plan from available plans for features
            let planFeatures = [];
            const matchingPlan = plansData.find(p => 
              p.id === subData.stripePriceId || 
              p.id === subData.plan?.id ||
              p.planId === subData.plan?.planId
            );
            
            if (matchingPlan && matchingPlan.features) {
              planFeatures = matchingPlan.features;
            } else if (subData.plan && Array.isArray(subData.plan.features)) {
              planFeatures = subData.plan.features;
            }
            
            const subscriptionPrice = 
              subData.plan?.amount || 
              subData.plan?.price || 
              (matchingPlan ? matchingPlan.price : null);
            
            // Set subscription with actual data
            setSubscription({
              id: subData._id || subData.id || subData.stripeSubscriptionId || 'sub_current',
              stripeSubscriptionId: subData.stripeSubscriptionId,
              planId: subData.stripePriceId || subData.plan?.id,
              planName: subData.plan?.name || (matchingPlan ? matchingPlan.name : 'Standard Plan'),
              status: subData.status || 'active',
              currentPeriodStart: subData.currentPeriodStart,
              currentPeriodEnd: subData.currentPeriodEnd,
              cancelAtPeriodEnd: subData.cancelAtPeriodEnd || false,
              amount: subscriptionPrice,
              interval: subData.interval || subData.plan?.interval || 'month',
              features: planFeatures
            });
            
            setLoading(false);
            return;
          }
        } catch (fallbackError) {
          console.error("Fallback subscription method also failed:", fallbackError);
          
          // As a last resort, try to build subscription data from tenant context
          if (tenant && tenant.plan) {
            console.log('Building subscription from tenant context:', tenant);
            
            // Find matching plan from available plans
            const matchingPlan = plansData.find(p => 
              p.planId === tenant.plan || 
              p.id === tenant.plan || 
              p.name.toLowerCase().includes(tenant.plan.toLowerCase())
            );
            
            if (matchingPlan) {
              console.log('Found matching plan from context:', matchingPlan);
              setSubscription({
                id: 'sub_from_tenant',
                planId: matchingPlan.id || tenant.plan,
                planName: matchingPlan.name || tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1) + ' Plan',
                status: tenant.subscriptionStatus || 'active',
                currentPeriodStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                currentPeriodEnd: tenant.subscriptionEndDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                cancelAtPeriodEnd: false,
                amount: matchingPlan.price,
                interval: matchingPlan.interval || 'month',
                features: matchingPlan.features || []
              });
            } else {
              // No subscription found - set to null
              setSubscription(null);
            }
          } else {
            // No subscription data available
            setSubscription(null);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch subscription data:", err);
        setError("Failed to load subscription information. Please refresh and try again.");
        setLoading(false);
      }
    };
    
    fetchSubscriptionData();
  }, [tenant]); // Remove plans from dependency array to prevent continuous looping
  
  const handleRefreshPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pull fresh plans from SuperAdmin API
      const plansResponse = await stripeService.getAdminSubscriptionPlans();
      
      if (plansResponse && plansResponse.success && Array.isArray(plansResponse.data)) {
        if (plansResponse.data.length > 0) {
          // Sort plans by price (lowest to highest)
          const sortedPlans = [...plansResponse.data].sort((a, b) => a.price - b.price);
          setPlans(sortedPlans);
          
          // Show user a success message
          setSuccess("Subscription plans refreshed successfully");
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError("No subscription plans are currently available. Please contact support for assistance.");
        }
      } else {
        setError("Failed to refresh subscription plans. Please try again.");
      }
    } catch (err) {
      console.error("Failed to refresh subscription data:", err);
      setError("Failed to refresh subscription plans. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateCheckoutSession = async (planId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Creating checkout session for plan: ${planId}`);
      
      // Check if planId is a product ID (starts with "prod_") instead of a price ID
      // This should now be handled in the service but we'll add a log here for clarity
      if (planId.startsWith('prod_')) {
        console.log(`Plan ID appears to be a product ID (${planId}). The service will handle conversion to price ID.`);
      }
      
      // Create success and cancel URLs based on current origin
      const successUrl = `${window.location.origin}/admin/subscription/success`;
      const cancelUrl = `${window.location.origin}/admin/subscription/cancel`;
      
      console.log(`Success URL: ${successUrl}`);
      console.log(`Cancel URL: ${cancelUrl}`);
      
      // Get current user's email for tenant admin email
      const userData = authService.getCurrentUser();
      const adminEmail = userData?.email;
      
      if (!adminEmail) {
        console.warn("No admin email found in user data, this might cause validation issues");
      } else {
        console.log(`Using admin email: ${adminEmail} for checkout session`);
      }
      
      // Create checkout session using the Stripe service with adminEmail included
      const response = await stripeService.createCheckoutSession(
        planId,
        successUrl,
        cancelUrl,
        null,  // tenantId parameter, let the system use the current tenant
        { adminEmail } // Add adminEmail as an additional option
      );
      
      console.log('Checkout session response:', response);
      
      // Handle all possible response formats
      let checkoutUrl = null;
      
      if (response?.data?.url) {
        // Format 1: { data: { url: "..." } }
        checkoutUrl = response.data.url;
      } else if (response?.data?.checkoutUrl) {
        // Format 2: { data: { checkoutUrl: "..." } }
        checkoutUrl = response.data.checkoutUrl;
      } else if (response?.success && response?.checkoutUrl) {
        // Format 3: { success: true, checkoutUrl: "..." }
        checkoutUrl = response.checkoutUrl;
      } else if (response?.checkoutUrl) {
        // Format 4: { checkoutUrl: "..." }
        checkoutUrl = response.checkoutUrl;
      } else if (response?.url) {
        // Format 5: { url: "..." }
        checkoutUrl = response.url;
      }
      
      // Redirect to checkout URL if available
      if (checkoutUrl) {
        console.log(`Redirecting to checkout URL: ${checkoutUrl}`);
        window.location.href = checkoutUrl;
      } else {
        console.error("Invalid checkout session response format:", response);
        throw new Error('No checkout URL received from server');
      }
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      setError("Failed to initiate subscription checkout. Please try again later.");
      
      // Log detailed error info for debugging
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
        
        // If there's a specific error message from the backend, show it
        if (err.response.data && (err.response.data.message || err.response.data.error)) {
          setError(`Checkout error: ${err.response.data.message || err.response.data.error}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    try {
      setCancelDialogOpen(false);
      setSubscriptionLoading(true);
      setError(null);
      
      if (!subscription || !subscription.id) {
        setError("Could not find subscription details to cancel.");
        setSubscriptionLoading(false);
        return;
      }
      
      console.log(`Attempting to cancel subscription with ID: ${subscription.id}`);
      
      // Check if we have a Stripe subscription ID - use that if available
      const subscriptionId = subscription.stripeSubscriptionId || subscription.id;
      
      // Call the cancel subscription API
      const response = await api.post(
        `/v1/stripe/tenant/subscriptions/${subscriptionId}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Subscription cancellation response:', response);
      
      // Update subscription state to show as cancelling
      setSubscription(prev => ({
        ...prev,
        cancelAtPeriodEnd: true
      }));
      
      // Show success message to user
      setSuccess("Your subscription has been set to cancel at the end of the current billing period.");
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
      
      // Fallback to stripeService method if direct API call fails
      try {
        console.log("Direct API call failed, trying stripeService.cancelSubscription");
        if (subscription && subscription.id) {
          await stripeService.cancelSubscription(subscription.id);
          setSubscription(prev => ({
            ...prev,
            cancelAtPeriodEnd: true
          }));
          setSuccess("Your subscription has been set to cancel at the end of the current billing period.");
          setTimeout(() => setSuccess(null), 5000);
        }
      } catch (fallbackErr) {
        console.error("Fallback cancelSubscription also failed:", fallbackErr);
        setError("Failed to cancel subscription. Please contact support.");
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };
  
  const handleResumeSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      if (subscription && subscription.id) {
        const response = await stripeService.resumeSubscription(subscription.id);
        if (response && response.data) {
          setSubscription({
            ...subscription,
            cancelAtPeriodEnd: false,
            status: response.data.status || 'active'
          });
          setSuccess("Your subscription has been resumed successfully.");
          setTimeout(() => setSuccess(null), 3000);
        }
      }
    } catch (err) {
      console.error("Failed to resume subscription:", err);
      setError("Failed to resume subscription. Please contact support.");
    } finally {
      setSubscriptionLoading(false);
    }
  };
  
  const handleChangePlan = () => {
    // Scroll to the available plans section
    const plansSection = document.getElementById('available-plans');
    if (plansSection) {
      plansSection.scrollIntoView({ behavior: 'smooth' });
      
      // Highlight the section briefly to draw attention
      plansSection.style.transition = 'background-color 0.5s ease';
      plansSection.style.backgroundColor = 'rgba(126, 87, 194, 0.1)';
      setTimeout(() => {
        plansSection.style.backgroundColor = '';
      }, 1500);
      
      // Add a success message to guide the user
      setSuccess("Please select a new plan from the options below.");
      setTimeout(() => setSuccess(null), 5000);
    } else {
      console.error("Could not find the available plans section in the DOM");
      setError("Could not locate plan options. Please refresh the page and try again.");
    }
  };
  
  const openCancelDialog = () => {
    setCancelDialogOpen(true);
  };
  
  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f5f7fa',
        }}
      >
        {error && (
          <Alert 
            severity={error.includes('cancel') ? "info" : "error"} 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ 
            p: 2, 
            bgcolor: subscription?.cancelAtPeriodEnd ? '#ffebee' : '#e8f5e9', 
            borderBottom: 1, 
            borderColor: 'divider' 
          }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              {subscription?.cancelAtPeriodEnd ? (
                <><CancelIcon sx={{ mr: 1, color: '#d32f2f' }} /> Subscription Canceling</>
              ) : subscription ? (
                <><CheckCircleIcon sx={{ mr: 1, color: '#43a047' }} /> Current Subscription</>
              ) : (
                <><WarningIcon sx={{ mr: 1, color: '#ff9800' }} /> No Active Subscription</>
              )}
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            {subscription ? (
              <>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Plan
                        </Typography>
                        <Typography variant="h5" gutterBottom>
                          {subscription.planName}
                          <Chip 
                            size="small" 
                            label={subscription.status.toUpperCase()}
                            color={subscription.status === 'active' ? "success" : "default"}
                            sx={{ ml: 1, height: 20 }}
                          />
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                          Billing
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(subscription.amount)} / {subscription.interval}
                        </Typography>
                        
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                          <EventIcon color="action" fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Current period: {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                          </Typography>
                        </Box>
                        
                        {subscription.cancelAtPeriodEnd && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            Your subscription will end on {formatDate(subscription.currentPeriodEnd)}
                          </Alert>
                        )}
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="outlined" 
                            color="primary"
                            onClick={handleChangePlan}
                          >
                            Change Plan
                          </Button>
                          {!subscription.cancelAtPeriodEnd ? (
                            <Button 
                              variant="outlined" 
                              color="error"
                              onClick={openCancelDialog}
                              disabled={subscriptionLoading}
                            >
                              Cancel Plan 
                            </Button>
                          ) : (
                            <Button 
                              variant="contained" 
                              color="primary"
                              onClick={handleResumeSubscription}
                              disabled={subscriptionLoading}
                            >
                              {subscriptionLoading ? <CircularProgress size={20} /> : 'Resume Subscription'}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Included Features
                        </Typography>
                        <List dense disablePadding>
                          {subscription.features && subscription.features.length > 0 ? (
                            subscription.features.map((feature, index) => (
                              <PlanFeature key={index} feature={feature} />
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText primary="Standard features included" />
                            </ListItem>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No active subscription found
                </Typography>
                {tenant && tenant.plan ? (
                  <>
                    <Typography variant="body1" mb={1}>
                      Current Plan: <Chip 
                        size="small"
                        label={tenant.plan?.toUpperCase() || "BASIC"}
                        color="primary" 
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Your organization is currently on the {tenant.plan ? tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1) : "Basic"} plan.
                      Choose a subscription plan below to access additional premium features.
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Choose a subscription plan to access premium features for your organization
                  </Typography>
                )}
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleChangePlan}
                  sx={{
                    mt: 2,
                    background: 'linear-gradient(45deg, #7e57c2 30%, #9575cd 90%)',
                    boxShadow: '0 4px 6px rgba(126, 87, 194, 0.25)',
                  }}
                >
                  Choose a Plan
                </Button>
              </Box>
            )}
          </Box>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center'
              }}>
                <HistoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6">Payment History</Typography>
              </Box>
              
              {paymentHistory && paymentHistory.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Receipt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((payment) => (
                        <TableRow key={payment.id || payment._id} hover>
                          <TableCell>{formatDate(payment.date || payment.createdAt)}</TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={(payment.status || 'completed').toUpperCase()}
                              color={
                                payment.status === 'succeeded' || payment.status === 'completed' ? "success" : 
                                payment.status === 'failed' ? "error" : 
                                payment.status === 'pending' ? "warning" : "default"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            {payment.receiptUrl ? (
                              <Button 
                                size="small" 
                                variant="text" 
                                href={payment.receiptUrl}
                                target="_blank"
                                startIcon={<ReceiptIcon fontSize="small" />}
                              >
                                Receipt
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No receipt
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No payment history found</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper id="available-plans" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Available Plans</Typography>
                <Button 
                  size="small"
                  onClick={handleRefreshPlans}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Refresh'}
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Upgrade your plan to access more features
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              {plans.length > 0 ? plans.map(plan => (
                <Card 
                  key={plan.id} 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    borderColor: plan.recommended ? 'primary.main' : 'inherit',
                    position: 'relative',
                    overflow: 'visible'
                  }}
                >
                  {plan.recommended && (
                    <Chip 
                      label="Recommended" 
                      size="small"
                      color="primary"
                      sx={{ 
                        position: 'absolute',
                        right: 8,
                        top: -10,
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h5" gutterBottom>
                      {formatCurrency(plan.price)} <Typography component="span" variant="body2">/{plan.interval}</Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {plan.description}
                    </Typography>
                    <Button 
                      variant={plan.recommended ? "contained" : "outlined"} 
                      color="primary"
                      fullWidth
                      onClick={() => handleCreateCheckoutSession(plan.id)}
                      endIcon={<ArrowIcon />}
                    >
                      {subscription ? 'Change Plan' : 'Choose Plan'}
                    </Button>
                  </CardContent>
                </Card>
              )) : (
                <Alert severity={error ? "error" : "info"} sx={{ mb: 2 }}>
                  {error || "No plans are currently available. Please check back later."}
                </Alert>
              )}
              
              {error && (
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => window.location.reload()}
                  sx={{ mt: 2 }}
                >
                  Refresh Plans
                </Button>
              )}
                   
              {/* Add support contact button */}
              <Button 
                variant="outlined"
                color="primary"
                fullWidth
                href="mailto:support@example.com"
                sx={{ mt: plans.length === 0 ? 2 : 0 }}
              >
                Contact Support
              </Button>
            </Paper>
            
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader title="Need Help?" />
              <CardContent>
                <Typography variant="body2" paragraph>
                  Questions about billing or your subscription? Our team is here to help.
                </Typography>
                <Button 
                  variant="outlined"
                  color="primary"
                  fullWidth
                  href="mailto:support@example.com"
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Cancel Subscription Confirmation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={closeCancelDialog}
          aria-labelledby="cancel-dialog-title"
          aria-describedby="cancel-dialog-description"
        >
          <DialogTitle id="cancel-dialog-title">
            {"Cancel Subscription?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-dialog-description">
              Are you sure you want to cancel your subscription? Your access will continue until the end of the current billing period on {formatDate(subscription?.currentPeriodEnd)}.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCancelDialog} color="primary">
              Keep Subscription
            </Button>
            <Button onClick={handleCancelSubscription} color="error" autoFocus>
              Cancel Subscription
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default SubscriptionManagement;
